import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismIcon } from '@mylighthouse/prism-ember';
import { toggle } from '@nullvoxpopuli/ember-composable-helpers';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import type * as Highcharts from 'highcharts';
import GeneralChartsLegendsLegendItem from 'lumen/components/general/charts/legends/legend-item';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import MarketTotalRevenueStrategyChartSegmentPopoverHowToRead from 'lumen/components/market/total-revenue/strategy/chart-segment/popover-how-to-read';
import type { ReferencePeriod } from 'lumen/controllers/market/total-revenue';
import type { TotalRevenueSegmentationBreakdownItem } from 'lumen/models';
import { CLASSIFICATION_TRANSLATIONS } from 'lumen/utils/constants/total-revenue-constants';
import safeDivide from 'lumen/utils/numeric/safe-divide';

type ChartWithCustomLabels = Highcharts.Chart & {
  customLabels?: Highcharts.SVGElement[];
};

interface MarketTotalRevenueStrategyChartSegmentSignature {
  Args: {
    onUpdateChartReference: (chart: Highcharts.Chart) => void;
    segmentationBreakdownItems: TotalRevenueSegmentationBreakdownItem[];
    selectedReferenceType: ReferencePeriod;
  };
}

export default class MarketTotalRevenueStrategyChartSegment extends Component<MarketTotalRevenueStrategyChartSegmentSignature> {
  @service declare intl: Services['intl'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isDecreaseSeriesVisible = true;
  @tracked isIncreaseSeriesVisible = true;

  /**
   * Takes the average of nrOfRooms excluding segments with 0 rooms.
   * Documented in https://lighthouseintelligence.atlassian.net/browse/TOR-527
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get averageNrOfRooms() {
    const filteredNrOfRooms = this.filteredData
      .map((item) => item.nrOfRooms)
      .filter((nrOfRooms) => nrOfRooms !== 0);

    const nrOfRoomsSum = filteredNrOfRooms.reduce((acc, item) => acc + item, 0);

    return safeDivide(nrOfRoomsSum, filteredNrOfRooms.length) ?? 0;
  }

  /**
   * Calculates sum(totalRevenue) / sum(nrOfRooms)
   * Documented in https://lighthouseintelligence.atlassian.net/browse/TOR-527
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get averageTotalRevenuePerRoomNight() {
    const totalRevenueSum = this.filteredData.reduce(
      (acc, item) => acc + item.totalRevenue,
      0,
    );
    const nrOfRoomsSum = this.filteredData.reduce(
      (acc, item) => acc + item.nrOfRooms,
      0,
    );

    return safeDivide(totalRevenueSum, nrOfRoomsSum) ?? 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const increaseInTotalRevenueSeries = {
      id: 'increase',
      color: `var(--prism-color-chart-status-figure-success-normal')`,
      visible: this.isIncreaseSeriesVisible,
      marker: {
        radius: 6,
      },
      data: [] as { name: string; x: number | null; y: number | null }[],
    };

    const decreaseInTotalRevenueSeries = {
      id: 'decrease',
      color: `var(--prism-color-chart-status-figure-critical-normal')`,
      visible: this.isDecreaseSeriesVisible,
      marker: {
        radius: 6,
      },
      data: [] as { name: string; x: number | null; y: number | null }[],
    };

    const noChangeInTotalRevenueSeries = {
      id: 'no-change',
      color: `var(--prism-color-chart-accent-primary-figure-default')`,
      marker: {
        radius: 6,
      },
      data: [] as { name: string; x: number | null; y: number | null }[],
    };

    this.filteredData.forEach((segmentationBreakdownItem) => {
      const referenceValue = segmentationBreakdownItem.referenceValues.find(
        (reference) =>
          reference.referenceType === this.args.selectedReferenceType,
      );

      if (!referenceValue) {
        return;
      }

      const point = {
        x: segmentationBreakdownItem.totalRevenuePerRoomNight,
        y: segmentationBreakdownItem.nrOfRooms,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        name: segmentationBreakdownItem.formattedName,
        classification: segmentationBreakdownItem.classification,
      };

      if (referenceValue.totalRevenueChange < 0) {
        decreaseInTotalRevenueSeries.data.push(point);
      } else if (referenceValue.totalRevenueChange === 0) {
        noChangeInTotalRevenueSeries.data.push(point);
      } else {
        increaseInTotalRevenueSeries.data.push(point);
      }
    });

    return [
      increaseInTotalRevenueSeries,
      decreaseInTotalRevenueSeries,
      noChangeInTotalRevenueSeries,
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    return {
      chart: {
        type: 'scatter',
        height: 412, // Matches design
        spacingBottom: 0,
        zoomType: undefined,
        events: {
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          render: (event: Event) =>
            this.drawChartQuadrantLabels(
              event.target as unknown as ChartWithCustomLabels,
            ),
        },
      },
      xAxis: {
        title: {
          text: this.intl.t(
            'total_revenue.market_segment.total_revenue_per_room_night',
          ),
        },
        gridLineWidth: 0.5, // Matches design
        gridLineDashStyle: 'Dash' as const,
        plotLines: [
          {
            value: this.averageTotalRevenuePerRoomNight,
            color: `var(--prism-color-general-neutral-800')`,
            dashStyle: 'Dash' as const,
            zIndex: 5, // Above the grid lines
          },
        ],
      },
      yAxis: {
        title: {
          text: this.intl.t('general.room_nights'),
        },
        gridLineWidth: 0.5, // Matches design
        gridLineDashStyle: 'Dash' as const,
        plotLines: [
          {
            value: this.averageNrOfRooms,
            color: `var(--prism-color-general-neutral-800')`,
            dashStyle: 'Dash' as const,
            zIndex: 5, // Above the grid lines
          },
        ],
      },
      tooltip: {
        shared: true,
        borderWidth: 0,
        shadow: false,
        padding: 12,
        backgroundColor: `var(--prism-color-background-inverse')`,
        style: {
          color: `var(--prism-color-text-inverse')`,
          fontSize: `var(--prism-font-size-text-200-regular')`,
          lineHeight: `var(--prism-font-line-height-text-200-regular')`,
        },
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter(this: Highcharts.TooltipFormatterContextObject) {
          return this.point.options.name as string;
        },
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        series: {
          point: {
            events: {
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              mouseOver(this: Highcharts.Point) {
                self.usageAnalytics.track('Production By Type Explored', {
                  eventTrigger: 'production-insight',
                  classification:
                    'classification' in this && this.classification,
                });
              },
            },
          },
        },
      },
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredData() {
    return this.args.segmentationBreakdownItems.filter((item) =>
      Boolean(item.classification),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  drawChartQuadrantLabels(chart: ChartWithCustomLabels) {
    const xAxis = chart.xAxis[0];
    const yAxis = chart.yAxis[0];

    if (!xAxis || !yAxis) {
      return;
    }

    // Remove existing custom labels
    if (chart.customLabels) {
      chart.customLabels.forEach((label) => label.destroy());
    }

    const fontSizeWithUnit = `var(--prism-font-size-text-200-regular')`;

    const padding = parseInt(`var(--prism-spacing-200')`, 10);
    const fontSize = parseInt(fontSizeWithUnit, 10);

    const xStart = xAxis.toPixels(xAxis.min ?? 0, false) + padding;
    const xAverage =
      xAxis.toPixels(this.averageTotalRevenuePerRoomNight, false) + padding;

    const yStart = yAxis.toPixels(yAxis.max ?? 0, false) + fontSize + padding;
    const yAverage =
      yAxis.toPixels(this.averageNrOfRooms, false) + fontSize + padding;

    const labels = [
      {
        text: this.intl.t(CLASSIFICATION_TRANSLATIONS['upsell']),
        x: xStart,
        y: yStart,
      },
      {
        text: this.intl.t(CLASSIFICATION_TRANSLATIONS['nurture']),
        x: xAverage,
        y: yStart,
      },
      {
        text: this.intl.t(CLASSIFICATION_TRANSLATIONS['market']),
        x: xAverage,
        y: yAverage,
      },
      {
        text: this.intl.t(CLASSIFICATION_TRANSLATIONS['reconsider']),
        x: xStart,
        y: yAverage,
      },
    ];

    chart.customLabels = labels.map((label) => {
      return chart.renderer
        .text(label.text, label.x, label.y)
        .attr({ zIndex: 5 }) // Above the grid lines
        .css({
          color: `var(--prism-color-text-neutral-subdued')`,
          fontSize: fontSizeWithUnit,
        })
        .add();
    });
  }

  <template>
    <div class="flex flex-col gap-300">
      <GeneralChartsOtaChart
        @chartOptions={{this.chartOptions}}
        @model={{this.chartData}}
        @onUpdateChartReference={{@onUpdateChartReference}}
      />

      <div class="flex items-center justify-center">
        <GeneralChartsLegendsLegendItem
          @id="positive-change"
          @isHidden={{not this.isIncreaseSeriesVisible}}
          @legendItemClicked={{toggle "isIncreaseSeriesVisible" this}}
          @type="dot"
        >
          {{t "total_revenue.increase_in_total_revenue"}}
        </GeneralChartsLegendsLegendItem>
        <GeneralChartsLegendsLegendItem
          @id="negative-change"
          @isHidden={{not this.isDecreaseSeriesVisible}}
          @legendItemClicked={{toggle "isDecreaseSeriesVisible" this}}
          @type="dot"
        >
          {{t "total_revenue.decrease_in_total_revenue"}}
        </GeneralChartsLegendsLegendItem>
        <button
          class="button--unstyled prism-link prism-link--secondary prism-link--small"
          data-test-button="How to read this chart"
          type="button"
        >
          <PrismIcon @name="circle-question-mark" @size="small" />
          {{t "total_revenue.how_to_read_this_chart"}}
          <MarketTotalRevenueStrategyChartSegmentPopoverHowToRead />
        </button>
      </div>
    </div>
  </template>
}
